import { Capacitor } from '@capacitor/core';
import { Network } from '@capacitor/network';

/**
 * Conectividad real en la app nativa.
 *
 * `navigator.onLine` en un WebView de Android es una fuente de verdad pobre:
 * llega tarde o no llega en las transiciones que más importan en carretera —
 * salir de un túnel, pasar de modo avión a datos, o un portal cautivo que
 * responde a todo con una pantalla de login. El síntoma que reporta el
 * operario es siempre el mismo: "ya tengo cobertura pero el aviso sigue
 * diciendo que hay pendientes, y hasta que no le doy a Sincronizar no sube
 * nada".
 *
 * El plugin de red de Capacitor escucha el `ConnectivityManager` de Android,
 * que sí conoce el estado real de la interfaz. Se usa como fuente PRINCIPAL en
 * nativo y se deja el evento del WebView como refuerzo: si cualquiera de los
 * dos dice que hemos vuelto, se intenta sincronizar. Un falso positivo solo
 * cuesta un reintento fallido; un falso negativo deja el trabajo del operario
 * sin subir.
 *
 * En web es un no-op: `subscribe` no hace nada y la app sigue con
 * `navigator.onLine` como siempre.
 */
export function isNativeNetworkAvailable(): boolean {
  return Capacitor.isNativePlatform();
}

/**
 * Notifica los cambios de conectividad del sistema. Devuelve la función para
 * dejar de escuchar. En web no engancha nada.
 */
export function subscribeToNativeNetwork(
  onChange: (connected: boolean) => void
): () => void {
  if (!isNativeNetworkAvailable()) return () => undefined;

  let handle: { remove: () => Promise<void> } | null = null;
  let cancelled = false;

  void Network.addListener('networkStatusChange', (status) => {
    onChange(status.connected);
  }).then((h) => {
    if (cancelled) {
      void h.remove();
      return;
    }
    handle = h;
  });

  // Estado inicial: el WebView puede arrancar con `navigator.onLine` en un
  // valor obsoleto heredado del arranque del proceso.
  void Network.getStatus().then((status) => {
    if (!cancelled) onChange(status.connected);
  });

  return () => {
    cancelled = true;
    if (handle) void handle.remove();
  };
}
